import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { formatTime } from '../utils/time';
import './ShareView.css';

export default function ShareView() {
  const { token } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportMsg, setReportMsg] = useState('');
  const [reportSent, setReportSent] = useState(false);

  const REPORT_REASONS = [
    {
      id: 'copyright',
      label: 'Urheberrechtsverletzung / Fremdes Eigentum',
      hint: 'Inhalt stammt aus einem Kochbuch, einer Zeitschrift oder einer anderen geschützten Quelle',
      placeholder: 'Link zum Originalwerk oder Name des Verlags / Autors (optional)',
    },
    {
      id: 'privacy',
      label: 'Verstoß gegen Datenschutz / Persönlichkeitsrechte',
      hint: 'Foto zeigt Personen oder enthält private Daten (Namen, Adressen, …)',
      placeholder: 'Beschreibe kurz welche personenbezogenen Daten betroffen sind',
    },
    {
      id: 'illegal',
      label: 'Illegale oder gefährliche Inhalte',
      hint: 'Inhalte die gegen Gesetze verstoßen oder zur Herstellung gefährlicher Substanzen anleiten',
      placeholder: 'Kurze Beschreibung des Verstoßes',
    },
    {
      id: 'spam',
      label: 'Spam / Betrug / Werbung',
      hint: 'Kein echtes Rezept — Werbung, Irreführung oder sinnloser Inhalt',
      placeholder: 'Optionale Anmerkung',
    },
  ];

  useEffect(() => {
    api.getSharedRecipeByToken(token)
      .then(data => {
        // ingredients können strukturiert oder als structured_json kommen
        setRecipe(normalizeRecipe(data));
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  function normalizeRecipe(data) {
    let ingredients = data.ingredients || [];
    // Falls structured_json statt ingredients
    if (!ingredients.length && data.structured_json) {
      try {
        const s = typeof data.structured_json === 'string'
          ? JSON.parse(data.structured_json) : data.structured_json;
        ingredients = (s.sections || [])
          .filter(sec => sec.type === 'ingredients')
          .flatMap(sec => sec.items || []);
      } catch { ingredients = []; }
    }
    let steps = data.steps || [];
    if (!steps.length && data.structured_json) {
      try {
        const s = typeof data.structured_json === 'string'
          ? JSON.parse(data.structured_json) : data.structured_json;
        steps = (s.sections || [])
          .filter(sec => sec.type === 'steps')
          .flatMap(sec => sec.items || []);
      } catch { steps = []; }
    }
    return { ...data, ingredients, steps };
  }

  if (loading) return (
    <div className="share-loading">
      <div className="share-spinner" />
    </div>
  );

  if (error) return (
    <div className="share-error">
      <span className="share-error-icon">🔗</span>
      <h2>Link ungültig oder abgelaufen</h2>
      <p>Dieser Rezept-Link existiert nicht oder wurde vom Besitzer deaktiviert.</p>
      <Link to="/" className="share-home-btn">Zur Startseite</Link>
    </div>
  );

  const formatIngredient = (ing) => {
    if (typeof ing === 'string') return ing;
    const parts = [ing.amount, ing.unit, ing.name].filter(Boolean);
    return parts.join(' ');
  };

  function handleReport(e) {
    e.preventDefault();
    const reasonLabel = REPORT_REASONS.find(r => r.id === reportReason)?.label || reportReason;
    const subject = encodeURIComponent(`Verstoß melden: ${recipe?.title || token}`);
    const body = encodeURIComponent(
      `Gemeldeter Inhalt: ${window.location.href}\nGrund: ${reasonLabel}` +
      (reportMsg ? `\n\nDetails:\n${reportMsg}` : '')
    );
    window.open(`mailto:kontakt@strixly.at?subject=${subject}&body=${body}`);
    setReportSent(true);
  }

  return (
    <div className="share-page">
      {/* Header */}
      <header className="share-header">
        <Link to="/" className="share-logo">Strixly</Link>
        <span className="share-badge">Geteiltes Rezept</span>
      </header>

      <main className="share-content">
        {/* Rezept-Titel */}
        <div className="share-title-area">
          {recipe.category && <span className="share-category">{recipe.category}</span>}
          <h1 className="share-title">{recipe.title}</h1>

          <div className="share-meta">
            {recipe.servings > 0 && <span>👥 {recipe.servings} Portionen</span>}
            {recipe.prep_time > 0 && <span>⏱ Vorbereitung: {formatTime(recipe.prep_time)}</span>}
            {recipe.cook_time > 0 && <span>🍳 Zubereitung: {formatTime(recipe.cook_time)}</span>}
          </div>
        </div>

        {/* Bild */}
        {recipe.image_path && (
          <img
            src={recipe.image_path}
            alt={recipe.title}
            className="share-image"
          />
        )}

        <div className="share-body">
          {/* Zutaten */}
          {recipe.ingredients?.length > 0 && (
            <section className="share-section">
              <h2>Zutaten</h2>
              <ul className="share-ingredients">
                {recipe.ingredients.map((ing, i) => (
                  <li key={ing.name || i}>{formatIngredient(ing)}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Zubereitung */}
          {recipe.steps?.length > 0 && (
            <section className="share-section">
              <h2>Zubereitung</h2>
              <ol className="share-steps">
                {recipe.steps.map((step, i) => (
                  <li key={`step-${i}`}>{typeof step === 'string' ? step : (step.text || '')}</li>
                ))}
              </ol>
            </section>
          )}
        </div>

        {/* Rechtlicher Hinweis */}
        <div className="share-legal-note">
          <p>
            Dieser Inhalt wurde von einem Nutzer hochgeladen. Strixly übernimmt
            keine Haftung für Urheberrechtsfragen.{' '}
            <button className="report-link" onClick={() => setShowReport(true)}>
              Verstoß melden
            </button>
          </p>
        </div>

        {/* Meldeformular Modal */}
        {showReport && (
          <div className="report-overlay" onClick={() => setShowReport(false)}>
            <div className="report-modal" onClick={e => e.stopPropagation()}>
              <button className="report-close" onClick={() => setShowReport(false)}>✕</button>
              {reportSent ? (
                <div className="report-success">
                  <span>✓</span>
                  <h3>Meldung eingegangen</h3>
                  <p>Danke, dass du uns auf diesen Inhalt hingewiesen hast. Wir nehmen alle Meldungen ernst und prüfen den Inhalt so schnell wie möglich.</p>
                  <p className="report-success-note">Bei Urheberrechtsverstößen erhältst du innerhalb von 72 Stunden eine Rückmeldung.</p>
                  <button className="btn-report-submit" onClick={() => setShowReport(false)}>Schließen</button>
                </div>
              ) : (
                <form onSubmit={handleReport}>
                  <h3>Verstoß melden</h3>
                  <p className="report-hint">Wähle den Grund für deine Meldung:</p>

                  <div className="report-reasons">
                    {REPORT_REASONS.map(r => (
                      <label key={r.id} className={`report-reason ${reportReason === r.id ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="reason"
                          value={r.id}
                          checked={reportReason === r.id}
                          onChange={() => setReportReason(r.id)}
                          required
                        />
                        <div className="report-reason-text">
                          <span className="report-reason-label">{r.label}</span>
                          {r.hint && <span className="report-reason-hint">{r.hint}</span>}
                        </div>
                      </label>
                    ))}
                  </div>

                  {reportReason && (
                    <label className="report-label">
                      Zusätzliche Informationen <span className="report-optional">(optional)</span>
                      <textarea
                        className="report-textarea"
                        value={reportMsg}
                        onChange={e => setReportMsg(e.target.value)}
                        placeholder={REPORT_REASONS.find(r => r.id === reportReason)?.placeholder || ''}
                        rows={3}
                      />
                    </label>
                  )}

                  <button type="submit" className="btn-report-submit" disabled={!reportReason}>
                    Meldung absenden
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="share-cta">
          <p>Digitalisiere deine eigenen Rezepte — kostenlos während der Beta.</p>
          <Link to="/" className="share-cta-btn">Kostenlos ausprobieren →</Link>
        </div>
      </main>
    </div>
  );
}
